import {
	type QuerySnapshotResponse as DtoQuerySnapshotResponse,
	type ViewSnapshotRequest as DtoViewSnapshotRequest,
	type ViewSnapshotResponse as DtoViewSnapshotResponse,
	type QueryParsingFailure,
	Type,
	type Version,
	type ViewSnapshotError,
	isQueryParsingFailure,
	isViewSnapshotError,
	isViewSnapshotFetchTimeout,
	isViewSnapshotTooManyActiveQueries,
} from "~/models/dto";
import type { Time } from "~/tools/date-time/time";

import { metaDataComponentsOnly } from "../app/state/view/viewpoint/ViewPointHelpers";
import { type Api, querySnapshotFn } from "../common/openapi";

import { DEFAULT_QUERY_CLIENT_CONFIG, type UseFullQueryOptions } from "./react-query";

/**
 * Converts the DTO ViewSnapshotRequest to the OpenAPI SnapshotRequest format.
 */
function convertToApiViewSnapshotRequest(dtoRequest: DtoViewSnapshotRequest): Api.ViewSnapshotRequest {
	return {
		query: dtoRequest.query,
		queryVersion: dtoRequest.queryVersion,
		metadata: {
			groupingEnabled: dtoRequest.metadata.groupingEnabled,
			showIndirectRelations: dtoRequest.metadata.showIndirectRelations,
			minGroupSize: dtoRequest.metadata.minGroupSize,
			groupedByLayer: dtoRequest.metadata.groupedByLayer,
			groupedByDomain: dtoRequest.metadata.groupedByDomain,
			groupedByRelation: dtoRequest.metadata.groupedByRelation,
			queryTime: dtoRequest.metadata.queryTime ?? null,
			autoGrouping: dtoRequest.metadata.autoGrouping,
			connectedComponents: dtoRequest.metadata.connectedComponents,
			neighboringComponents: dtoRequest.metadata.neighboringComponents,
			showFullComponent: dtoRequest.metadata.showFullComponent,
		},
	};
}

/**
 * Deserializes the OpenAPI QuerySnapshotResult to extract the ViewSnapshotResponse.
 * The viewSnapshotResponse field is typed as an opaque object in OpenAPI but contains
 * the actual ViewSnapshotResponse DTO structure at runtime.
 */
function deserializeQuerySnapshotResponse(result: Api.QuerySnapshotResult): DtoQuerySnapshotResponse {
	// The viewSnapshotResponse is already in the correct DTO format at runtime,
	// we just need to cast it from the opaque type
	const viewSnapshotResponse = result.viewSnapshotResponse as unknown as DtoViewSnapshotResponse;

	return {
		_type: Type.QuerySnapshotResult,
		viewSnapshotResponse,
	};
}

/**
 * @param viewSnapshotRequest - The request describing the desired snapshot. When `undefined`, the query will be disabled.
 */
export const queryTopologySnapshot = ((viewSnapshotRequest) => {
	return {
		queryKey: ["topology-snapshot", viewSnapshotRequest] as const,
		// this is a POST request only because it submits lots of payload to query some data
		// but it does not update anything, so it is a "query" in terms of RQ
		queryFn: async () => {
			if (viewSnapshotRequest == null) {
				throw new Error("viewSnapshotRequest is required");
			}

			const apiRequest = convertToApiViewSnapshotRequest(viewSnapshotRequest);

			const result = await querySnapshotFn({
				body: apiRequest,
				timeoutMs: undefined,
			});

			// Handle QueryParsingFailure (discriminated union in OpenAPI response)
			if (result._type === ("QueryParsingFailure" as Api.QuerySnapshotResult.TypeEnum)) {
				const failure: QueryParsingFailure = {
					_type: Type.QueryParsingFailure,
					reason: (result as unknown as Api.QueryParsingFailure).reason,
				};
				throw failure;
			}

			const response = deserializeQuerySnapshotResponse(result);

			if (isQueryParsingFailure(response)) {
				throw response;
			}

			if (isViewSnapshotError(response.viewSnapshotResponse)) {
				throw response.viewSnapshotResponse;
			}

			return response.viewSnapshotResponse;
		},
		enabled: viewSnapshotRequest != null,
		timestamp: viewSnapshotRequest?.metadata.queryTime,
		retry: (failureCount, error: Error | QueryParsingFailure | ViewSnapshotError) => {
			if (isViewSnapshotTooManyActiveQueries(error) || isViewSnapshotFetchTimeout(error)) {
				return failureCount < 2;
			}

			return DEFAULT_QUERY_CLIENT_CONFIG.defaultOptions.queries.retry(failureCount, error);
		},
	} as const;
}) satisfies (viewSnapshotRequest: undefined | DtoViewSnapshotRequest) => UseFullQueryOptions;

export interface QueryViewRelatedComponentsFromTopologySnapshotParams {
	stql: string;
	componentId: number;
	timestamp: undefined | Time;
}

export const queryViewRelatedComponentsFromTopologySnapshot = (({
	stql,
	componentId,
	timestamp,
}: QueryViewRelatedComponentsFromTopologySnapshotParams) =>
	queryTopologySnapshot({
		_type: Type.ViewSnapshotRequest,
		query: `(${stql}) AND NOT id = "${componentId}"`,
		queryVersion: "0.0.1" as Version,
		metadata: metaDataComponentsOnly(timestamp),
	})) satisfies (params: QueryViewRelatedComponentsFromTopologySnapshotParams) => UseFullQueryOptions;
